import React from "react";
import { Timesheet } from "../../pages/types/timesheet";
import { Box, Card, CardContent, Divider, IconButton, Stack, TextField, Typography, Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { calcTotalHours } from "../../pages/services/timesheetsApi";

const DAYS = [
    { key: "mon", label: "Mon" },
    { key: "tue", label: "Tue" },
    { key: "wed", label: "Wed" },
    { key: "thu", label: "Thu" },
    { key: "fri", label: "Fri" },
    { key: "sat", label: "Sat" },
    { key: "sun", label: "Sun" },
] as const;

const toNumber = (v: any) => {
    const n = Number(v);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, n);
};

type Props = {
    value: Timesheet;
    onChange: (next: Timesheet) => void;
    readOnly?: boolean;
};

export default function TimesheetEditor({ value, onChange, readOnly = false }: Props) {
    const total = calcTotalHours(value);

    const updateLine = (idx: number, patch: any) => {
        const next: Timesheet = structuredClone(value);
        next.lines[idx] = { ...next.lines[idx], ...patch };
        onChange(next);
    };

    const addLine = () => {
        const next: Timesheet = structuredClone(value);
        next.lines.push({ project: "", task: "", mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 });
        onChange(next);
    };

    const deleteLine = (idx: number) => {
        const next: Timesheet = structuredClone(value);
        next.lines.splice(idx, 1);
        if (next.lines.length === 0) {
            next.lines = [{ project: "", task: "", mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 }];
        }
        onChange(next);
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                    <Typography sx={{ fontWeight: 900 }}>
                        Status: {value.status} • Total Hours: {total}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button startIcon={<AddIcon />} variant="outlined" onClick={addLine} disabled={readOnly}>
                        Add Row
                    </Button>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1.25}>
                    {value.lines.map((line, idx) => (
                        <Card key={idx} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
                                    <TextField
                                        label="Project"
                                        value={line.project}
                                        onChange={(e) => updateLine(idx, { project: e.target.value })}
                                        sx={{ minWidth: 200 }}
                                        disabled={readOnly}
                                    />
                                    <TextField
                                        label="Task"
                                        value={line.task}
                                        onChange={(e) => updateLine(idx, { task: e.target.value })}
                                        sx={{ minWidth: 220 }}
                                        disabled={readOnly}
                                    />

                                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                        {DAYS.map((d) => (
                                            <TextField
                                                key={d.key}
                                                label={d.label}
                                                type="number"
                                                value={(line as any)[d.key]}
                                                onChange={(e) => updateLine(idx, { [d.key]: toNumber(e.target.value) })}
                                                inputProps={{ min: 0, step: 0.5 }}
                                                sx={{ width: 90 }}
                                                disabled={readOnly}
                                            />
                                        ))}
                                    </Stack>

                                    <IconButton onClick={() => deleteLine(idx)} disabled={readOnly} aria-label="delete row">
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <TextField
                    label="Notes"
                    value={value.notes || ""}
                    onChange={(e) => onChange({ ...value, notes: e.target.value })}
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={readOnly}
                />
            </CardContent>
        </Card>
    );
}